import React, { useEffect } from 'react';
import Divider from '@material-ui/core/Divider';
import SearchFilter from '@alltrails/shared/components/SearchFilter';
import ExpandableFilterPanel from 'components/explore/ExpandableFilterPanel';
import FilterInputFactory from 'components/explore/filters/FilterInputFactory';
import SaveButton from 'components/explore/filters/SaveButton';
import useHypotheticalSearchResultsCount, { SearchResultsCountFunction } from 'hooks/useHypotheticalSearchResultsCount';
import useModalPortalElement from 'hooks/useModalPortalElement';
import useSearchFilters from 'hooks/useSearchFilters';
import { Filter, FilterConfig, FilterConfigStarRating, FilterInput, FilterRangeUpdates } from 'types/Search';
import * as styles from './styles/styles.module.scss';

export type Props = {
  clearRadioFilter: (filterKey: string, page: string) => void;
  handleFilterRadioToggle: (filterKey: string, configKey: string) => void;
  handleFilterSet: (filterKey: string, newValue: FilterConfigStarRating) => void;
  handleFilterSlider: (filterKey: string, changes: FilterRangeUpdates) => void;
  handleFilterToggle: (filterKey: string, configKey: string) => void;
  popperClassName?: string;
  page: string;
  resultsCount: number;
  searchResultsCountFunction: SearchResultsCountFunction;
  sortedFilters: Filter[];
  title: React.ReactNode;
  triggerElement?: JSX.Element;
};

/**
 * NestedPanel is like a special case of FilterPillFactory and could be collapsed into that.
 */
const NestedPanel = ({
  clearRadioFilter,
  handleFilterRadioToggle,
  handleFilterSet,
  handleFilterSlider,
  handleFilterToggle,
  page = '',
  popperClassName,
  resultsCount = 0,
  searchResultsCountFunction,
  sortedFilters = [],
  triggerElement,
  title
}: Props) => {
  const { activeFilters, clearDraftChanges, clearSavedFilters, filtersByKey, saveChanges, updateFilter } = useSearchFilters({
    clearRadioFilter,
    filters: sortedFilters,
    handleFilterRadioToggle,
    handleFilterSet,
    handleFilterSlider,
    handleFilterToggle,
    page
  });

  let displayText;
  const isActive = activeFilters.length > 0;
  switch (activeFilters.length) {
    case 0:
      displayText = title;
      break;
    default:
      displayText = `${title} • ${activeFilters.length}`;
      break;
  }

  const { performHypotheticalSearch, computedResultsCount, resetHypotheticalResultsCount } = useHypotheticalSearchResultsCount({
    resultsCount,
    searchResultsCountFunction
  });

  useEffect(() => {
    performHypotheticalSearch(filtersByKey);
  }, [filtersByKey]);

  const modalElement = useModalPortalElement();

  return (
    <SearchFilter
      clearButtonHandler={() => {
        clearSavedFilters();
        resetHypotheticalResultsCount();
      }}
      forceModalState
      hasActiveFilters={isActive}
      modalElement={modalElement}
      modalTitle={title}
      pillButtonText={displayText}
      popperClassName={popperClassName}
      resetCurrentFormStateHandler={() => {
        clearDraftChanges();
        resetHypotheticalResultsCount();
      }}
      saveButtonChildren={<SaveButton resultsCount={computedResultsCount} />}
      saveButtonHandler={saveChanges}
      triggerElement={triggerElement}
    >
      <>
        {sortedFilters.map((filter, index) => {
          // The UI treatment for star ratings is slightly different than other inputs.
          const panelTitle = filter.type === FilterInput.STAR_RATING ? null : filter.display;

          return (
            <>
              <ExpandableFilterPanel
                key={filter.key}
                className={styles.filterPanel}
                tabIndex={index}
                title={panelTitle}
                shouldTruncate={filter.truncate}
              >
                <FilterInputFactory filter={filtersByKey[filter.key]} onChange={(changes: FilterConfig) => updateFilter(filter.key, changes)} />
              </ExpandableFilterPanel>
              {index !== sortedFilters.length - 1 && <Divider key={`divider-${filter.key}-${sortedFilters.length}`} />}
            </>
          );
        })}
      </>
    </SearchFilter>
  );
};

export default NestedPanel;
